import React, { useEffect, useState, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert, Modal } from 'react-bootstrap';
import './Quizzes.css';
import { getQuizzes, getUserProfile, createCheckoutSession } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { trackEvent } from '../../components/Tools/analytics';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const Quizzes = () => {
    const [quizzes, setQuizzes] = useState([]);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedQuizId, setSelectedQuizId] = useState(null);
    const [selectedQuizTitle, setSelectedQuizTitle] = useState('');
    const navigate = useNavigate();

    const handleBuyClick = (id, title) => {
        setSelectedQuizId(id);
        setSelectedQuizTitle(title);
        setShowConfirmModal(true);
    };

    const confirmPayment = async () => {
        setShowConfirmModal(false);
        await handlePayment(selectedQuizId);
    };

    const fetchUserProfileAndQuizzes = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const [profileResponse, quizzesResponse] = await Promise.all([
                getUserProfile(),
                getQuizzes()
            ]);

            setUserProfile(profileResponse.data);
            setQuizzes(quizzesResponse.data);
        } catch (error) {
            console.error('Quizzes: Lỗi khi tải dữ liệu:', error);
            setError('Không thể tải đề thi. Vui lòng thử lại sau.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUserProfileAndQuizzes();
        trackEvent('Viewed Quizzes Page');
    }, [fetchUserProfileAndQuizzes]);

    const handleStartTest = (id) => {
        const quiz = quizzes.find(q => q._id === id);
        trackEvent('Clicked Start Quiz', {
            quizId: id,
            quizTitle: quiz?.title || 'Unknown',
        });
        navigate(`/quizzes/${id}`);
    };

    const handlePayment = async (id) => {
        try {
            const quiz = quizzes.find(q => q._id === id);
            trackEvent('Clicked Buy Quiz', {
                quizId: id,
                quizTitle: quiz?.title || 'Unknown',
                quizPrice: quiz?.price || 0,
            });

            const response = await createCheckoutSession(id);
            const stripe = await stripePromise;
            const { error } = await stripe.redirectToCheckout({ sessionId: response.sessionId });
            if (error) {
                console.error('Stripe payment failed:', error);
            }
        } catch (error) {
            console.error('Error creating payment session:', error);
        }
    };

    const canAccessQuiz = (quiz) => {
        if (quiz.price === 0) return true;
        if (userProfile?.isPremium) return true;
        return false;
    };

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
                <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Đang tải đề thi...</p>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="my-5">
                <Alert variant="danger">
                    <Alert.Heading>Đã xảy ra lỗi</Alert.Heading>
                    <p>{error}</p>
                    <div className="d-flex justify-content-end">
                        <Button variant="outline-danger" onClick={() => fetchUserProfileAndQuizzes()}>
                            Thử lại
                        </Button>
                    </div>
                </Alert>
            </Container>
        );
    }

    return (
        <Container className="quizzes-container my-5">
            <h2 className="text-center mb-5 ">Đề Luyện Thi</h2>
            <Row className="mb-5">
                <Col lg={6} md={12}>
                    <Card className="shadow-sm h-100">
                        <Card.Body>
                            <Card.Title as="h3" className="mb-4 border-bottom pb-3">Giới thiệu Bộ đề thi</Card.Title>
                            <Card.Text>
                                Chào mừng bạn đến với trang luyện thi của Thi Nail.
                            </Card.Text>
                            <ul className="mb-4">
                                <li>Bộ Đề thi của Thinail Khác với bộ đề 900 câu trên các trang web khác.</li>
                                <li>Bạn Có thể bấm nộp bài để xem đáp án của các câu hỏi mà không cần phải hoàn thành hết.</li>
                                <li>Điểm của bạn sẽ hiện lên sau khi nộp bài.</li>
                                <li>Sau khi ôn tập ở đây bạn có thể bấm qua kênh thi thử để kiểm tra kiến thức</li>
                                <li>Bộ "Đề Thi Chuẩn (Demo)" Có các câu hỏi trong bộ "Đề Thi Chuẩn" Bạn có thể tham khảo để cân nhắc trước khi mua</li>
                            </ul>
                            <Card.Text className="text-info">
                                Vui lòng chọn một bài thi từ danh sách dưới đây và bắt đầu khi bạn đã sẵn sàng.
                            </Card.Text>
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={6} md={12}>
                    <Row>
                        {quizzes.length === 0 ? (
                            <Col>
                                <Alert variant="info">
                                    <Alert.Heading>Không có đề thi</Alert.Heading>
                                    <p>Hiện tại không có đề thi nào. Vui lòng quay lại sau.</p>
                                </Alert>
                            </Col>
                        ) : (
                            quizzes.map(quiz => (
                                <Col key={quiz._id} md={6} className="mb-4">
                                    <Card className="quiz-card h-100 shadow-sm border-0">
                                        <Card.Header className="bg-light">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <h5 className="mb-0">{quiz.title}</h5>
                                                {quiz.price > 0 && !userProfile?.isPremium && (
                                                    <Badge bg="info" className="ms-2">
                                                        ${quiz.price}
                                                    </Badge>
                                                )}
                                            </div>
                                        </Card.Header>
                                        <Card.Body className="d-flex flex-column">
                                            <Card.Text className="flex-grow-1">{quiz.description}</Card.Text>
                                            <div className="mt-auto">
                                                {canAccessQuiz(quiz) ? (
                                                    <Button
                                                        variant="primary"
                                                        onClick={() => handleStartTest(quiz._id)}
                                                        className="w-100"
                                                    >
                                                        Bắt Đầu Thi
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        variant="success"
                                                        onClick={() => handleBuyClick(quiz._id, quiz.title)}
                                                        className="w-100"
                                                    >
                                                        Mua Đề Thi
                                                    </Button>
                                                )}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))
                        )}
                    </Row>
                </Col>
            </Row>

            {userProfile?.isPremium && (
                <Alert variant="success" className="mt-3">
                    <Alert.Heading>Tài khoản Premium</Alert.Heading>
                    <p>
                        Bạn đang sử dụng tài khoản Premium. Bạn có thể truy cập tất cả các đề thi không giới hạn.
                    </p>
                </Alert>
            )}


            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ color: 'white !important' }}>Xác nhận mua đề thi</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Bạn sắp mua đề thi: <strong>{selectedQuizTitle}</strong>.
                    </p>
                    <ul>
                        <li>Bộ đề gồm <strong>125 câu hỏi</strong>, bám sát cấu trúc đề thi thật.</li>
                        <li>Sau khi thanh toán, bạn sẽ được truy cập vào cả <strong>Đề thi chuẩn</strong> và <strong>Đề thi chuẩn 2</strong>.</li>
                        <li>Bạn có thể luyện tập, xem đáp án và chấm điểm ngay sau khi hoàn thành bài thi.</li>
                    </ul>
                    <hr />
                    <small className="text-muted">
                        Mọi thanh toán được xử lý an toàn qua Stripe. Trang web <strong>không lưu trữ thông tin thẻ tín dụng</strong> của bạn.
                    </small>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Hủy
                    </Button>
                    <Button variant="primary" onClick={confirmPayment}>
                        Tiếp tục thanh toán
                    </Button>
                </Modal.Footer>
            </Modal>

        </Container>
    );
};

export default Quizzes;
